import React, { useContext } from "react";

import { CalculationTableRow } from "../components/calculation-table/CalculationTable";
import FetchGraphQL from "../components/graphql/FetchGraphQL";
import { ProportionsWithProductCount } from "../components/scope-3/ImpactRatingProportionsChart";
import AvailableIngredient from "../domain/AvailableIngredient";
import { ImpactRating } from "../domain/impactRatings";
import { MassUnit, Unit } from "../util/units";
import { SalesDataResponse } from "./useSalesData";

export interface ErrorResponse {
  success: false;
  error: {
    code: string;
  };
}

export interface ChangePasswordOptions {
  userId: number;
  username: string;
  oldPassword: string;
  newPassword: string;
}

export interface MaintenanceWindow {
  start: Date;
  end: Date;
  isActive: boolean;
}

export interface Category {
  name: string;
  spend: string | null;
  landUse: number;
  massKg: number;
  ghg: number;
  waterUse: number;
  landUseChangeCO2?: number;
  landManagementCO2?: number;
  landManagementCH4?: number;
  landManagementN2O?: number;
  flagTotal?: number;
  energy?: number;
}

export interface ProcuredItem {
  categoryName: string | null;
  category2Name: string | null;
  category3Name: string | null;
  ghg: number | null;
  impactRatingGhg: ImpactRating | null;
  impactRatingLandUse: ImpactRating | null;
  impactRatingWaterUse: ImpactRating | null;
  internalId: string;
  landUse: number | null;
  name: string;
  massKg: number | null;
  quantity: number | null;
  site: string | null;
  foodType: string | null;
  supplier: string | null;
  spend: string | null;
  unit: string | null;
  waterUse: number | null;
  landUseChangeCO2?: number;
  landManagementCO2?: number;
  landManagementCH4?: number;
  landManagementN2O?: number;
  flagTotal?: number;
  energy?: number;
}

export type ValueWithProportionalChange<T> = {
  value: T;
  proportionalChange?: number | null;
};

export interface ReportTotals {
  ghg: ValueWithProportionalChange<number>;
  waterUse: ValueWithProportionalChange<number>;
  landUse: ValueWithProportionalChange<number>;
  spend: ValueWithProportionalChange<number | null>;
}

export interface AssessmentTotals {
  assessmentId: string;
  ghg: number | null;
  waterUse: number | null;
  landUse: number | null;
  name: string;
  numItems: number;
}

export interface ReportExportInfo {
  downloadLink: string;
  numItems: string;
}

export interface ImpactRatingProportion {
  itemCount: number;
  valueWithProportionalChange: ValueWithProportionalChange<number | null>;
}

export interface ImpactRatingProportions {
  veryLow: ImpactRatingProportion | null;
  low: ImpactRatingProportion | null;
  medium: ImpactRatingProportion | null;
  high: ImpactRatingProportion | null;
  veryHigh: ImpactRatingProportion | null;
}
export interface AssessmentSummary extends ReportTotals {
  ghgPerSpend: ValueWithProportionalChange<number | null>;
  landUsePerSpend: ValueWithProportionalChange<number | null>;
  waterUsePerSpend: ValueWithProportionalChange<number | null>;
  spendCurrency: "EUR" | "GBP" | "USD" | null;
  ghgIntensity: ValueWithProportionalChange<number>;
  impactRatingGhg: ImpactRating | null;
  landUseIntensity: ValueWithProportionalChange<number>;
  impactRatingLandUse: ImpactRating | null;
  impactRatingWaterUse: ImpactRating | null;
  impactRatingProportions: {
    ghg: ImpactRatingProportions;
    landUse: ImpactRatingProportions;
    waterUse: ImpactRatingProportions;
  };
  waterUseIntensity: ValueWithProportionalChange<number>;
  comparisonAssessmentName: string | null;
  procuredItemsCount: ValueWithProportionalChange<number>;
  totalMass: ValueWithProportionalChange<number>;
}

export interface Assessment {
  id: string;
  name: string;
  created_at: string;
  period_covered_start: string;
  spend_currency: string;
  number_of_items_assessed: number;
  show_scope3_report: boolean;
  show_land_use_report: boolean;
  show_water_use_report: boolean;
  is_default: boolean;
}

export interface ProductsDashboardOrganization {
  id: string;
  name: string;
}

export interface ProductsDashboardData {
  averageIntensityGhg: number | null;
  averageIntensityImpactRatingGhg: ImpactRating | null;
  averageIntensityLandUse: number | null;
  averageIntensityImpactRatingLandUse: ImpactRating | null;
  averageIntensityWaterUse: number | null;
  averageIntensityImpactRatingWaterUse: ImpactRating | null;
  highestImpactProductGhg: DashboardProduct | null;
  lowestImpactProductGhg: DashboardProduct | null;
  highestImpactProductLandUse: DashboardProduct | null;
  lowestImpactProductLandUse: DashboardProduct | null;
  highestImpactProductWaterUse: DashboardProduct | null;
  lowestImpactProductWaterUse: DashboardProduct | null;
  numberOfDescendantOrganizations: number;
  numberOfProducts: number;
  tags: Array<DashboardProductTag>;
  dietaryCategories: Array<DashboardDietaryCategory>;
  hasExternallySyncedProducts: boolean;
  impactRatingProportions: {
    ghg: ProportionsWithProductCount;
    landUse: ProportionsWithProductCount;
    waterUse: ProportionsWithProductCount;
  };
}

export interface DashboardProduct {
  id: number;
  name: string;
  impactCached: {
    ghgPerKg: number | null;
    impactRatingGhg: ImpactRating | null;
    landUsePerKg: number | null;
    impactRatingLandUse: ImpactRating | null;
    waterUsePerKg: number | null;
    impactRatingWaterUse: ImpactRating | null;
  };
}

export interface DashboardProductTag {
  name: string;
  count: number;
  averageIntensityGhg: number | null;
  impactRatingGhg: ImpactRating | null;
  averageIntensityLandUse: number | null;
  impactRatingLandUse: ImpactRating | null;
  averageIntensityWaterUse: number | null;
  impactRatingWaterUse: ImpactRating | null;
}

export interface DashboardDietaryCategory {
  name: string;
  productCount: number;
}

export interface Supplier {
  name: string;
  spend: string;
  landUse: number;
  massKg: number;
  ghg: number;
  waterUse: number;
  landUseChangeCO2?: number;
  landManagementCO2?: number;
  landManagementCH4?: number;
  landManagementN2O?: number;
  flagTotal?: number;
  energy?: number;
}

export interface Site {
  name: string;
  spend: string;
  landUse: number;
  massKg: number;
  ghg: number;
  waterUse: number;
  landUseChangeCO2?: number;
  landManagementCO2?: number;
  landManagementCH4?: number;
  landManagementN2O?: number;
  flagTotal?: number;
  energy?: number;
}

export interface SupportedRegion {
  id: number;
  name: string;
}

export interface TagAverageImpactIntensity {
  tagName: string;
  ghg: number | null;
  landUse: number | null;
  waterUse: number | null;
}

export interface SalesDataTimePeriod {
  year: number;
  periodType: "Monthly" | "Quarterly" | "Half Yearly" | "Yearly";
  month?: number;
  quarter?: number;
  halfYear?: number;
}

export interface SalesDataTimePeriodsResponse {
  timePeriods: SalesDataTimePeriod[];
}

export type RatingDistribution = {
  veryLow: number;
  low: number;
  medium: number;
  high: number;
  veryHigh: number;
};

export type GhgEmissionsUnitTuple = [MassUnit, "co2e"];

export interface SalesRecord {
  salesDataTimePeriod: SalesDataTimePeriod;
  ghgEmissions: number;
  landUse: number;
  waterUse: number;
  sales: number;
  ratingDistribution: RatingDistribution;
  ghgEmissionsUnit: GhgEmissionsUnitTuple;
}

export type ChartPeriod = "MONTHLY" | "QUARTERLY" | "YEARLY";

export interface ImpactRatingValuePercentage {
  value: number;
  percentage: number;
}

export type RatingDistributionByCategory = {
  veryLow: ImpactRatingValuePercentage | null;
  low: ImpactRatingValuePercentage | null;
  medium: ImpactRatingValuePercentage | null;
  high: ImpactRatingValuePercentage | null;
  veryHigh: ImpactRatingValuePercentage | null;
};

export interface SalesDashboardData {
  success: true;
  monthlyData?: SalesRecord[];
  quarterlyData?: SalesRecord[];
  yearlyData: SalesRecord[];
  productsRatings?: {
    ghg: RatingDistributionByCategory;
    landUse: RatingDistributionByCategory;
    waterUse: RatingDistributionByCategory;
  };
  salesRatings?: {
    ghg: RatingDistributionByCategory;
    landUse: RatingDistributionByCategory;
    waterUse: RatingDistributionByCategory;
  };
}

export interface SalesDashboardSummaryDataField {
  value: number;
  proportionalChange?: number;
}

export interface SalesDashboardTagData {
  name: string;
  average_ghg_intensity: number;
  average_land_use_intensity: number;
  average_water_use_intensity: number;
  id: number;
  product_count: number;
  total_ghg_emissions: number;
  total_land_use: number;
  total_mass: number;
  total_sales: number;
  total_water_use: number;
}

export interface SalesDashboardTagsData {
  success: true;
  collections: SalesDashboardTagData[];
  ghgEmissionsUnit: Unit[];
  landUseUnit: Unit[];
  waterUseUnit: Unit[];
}

interface BestSellingProduct {
  productId: number;
  productName: string;
  impactIntensity: number;
}

interface BestSellingProducts {
  bestSellingARatedProduct: BestSellingProduct | null;
  bestSellingERatedProduct: BestSellingProduct | null;
}

export interface BestSellingProductData {
  ghg: BestSellingProducts;
  landUse: BestSellingProducts;
  waterUse: BestSellingProducts;
}

export interface SalesDashboardSummaryData {
  success: true;
  totalGhgEmissions: SalesDashboardSummaryDataField;
  totalLandUse: SalesDashboardSummaryDataField;
  totalWaterUse: SalesDashboardSummaryDataField;
  averageGhgIntensitySales: SalesDashboardSummaryDataField;
  averageLandUseIntensitySales: SalesDashboardSummaryDataField;
  averageWaterUseIntensitySales: SalesDashboardSummaryDataField;
  averageGhgIntensityProducts: SalesDashboardSummaryDataField;
  averageLandUseIntensityProducts: SalesDashboardSummaryDataField;
  averageWaterUseIntensityProducts: SalesDashboardSummaryDataField;
  numberOfProducts: SalesDashboardSummaryDataField;
  numberOfSales: SalesDashboardSummaryDataField;
  totalMass: SalesDashboardSummaryDataField;
  veryLowRatedSalesGhgPercentage: SalesDashboardSummaryDataField;
  veryLowRatedSalesLandUsePercentage: SalesDashboardSummaryDataField;
  veryLowRatedSalesWaterUsePercentage: SalesDashboardSummaryDataField;
  ghgEmissionsUnit: Unit[];
  landUseUnit: Unit[];
  waterUseUnit: Unit[];
  bestSellingProductData: BestSellingProductData;
}

export interface DataStore {
  fetchCalculationTable: (
    subjectId: string,
    subjectType: string,
    otherParams: URLSearchParams
  ) => Promise<{
    tableParameters: Map<string, string | number | null>;
    row: CalculationTableRow;
  }>;

  fetchGraphQL: FetchGraphQL;

  fetchAvailableIngredients: (
    organizationId: string | null
  ) => Promise<Array<AvailableIngredient>>;

  fetchMaintenanceWindows: () => Promise<Array<MaintenanceWindow>>;

  fetchMethodologySummaryUrl: () => Promise<string | null>;

  fetchPublicOrganizationData: (
    organizationId: string
  ) => Promise<{ organizationName: string }>;

  changePassword: (args: ChangePasswordOptions) => Promise<void>;

  fetchProductsDashboardData: (
    organizationId: string,
    includeDescendants: boolean,
    filterParams: string
  ) => Promise<ProductsDashboardData | null>;

  fetchProductsDashboardOrganizationOptions: (
    organizationId: string
  ) => Promise<Array<ProductsDashboardOrganization>>;

  fetchReportCategories: (
    organizationId: string,
    assessmentId?: string,
    filters?: string
  ) => Promise<Array<Array<Category>> | null>;

  fetchReportSites: (
    organizationId: string,
    assessmentId?: string,
    filters?: string
  ) => Promise<Array<Site> | null>;

  fetchReportExportUrl: (
    assessmentId: string,
    organizationId: string
  ) => Promise<ReportExportInfo | null>;

  fetchReportItems: (
    assessmentId: string,
    organizationId: string,
    pageSize?: number,
    sortColumn?: string,
    sortDirection?: "asc" | "desc",
    queryString?: string
  ) => Promise<Array<ProcuredItem> | null>;

  fetchReportTotals: (
    assessmentId: string,
    organizationId: string,
    queryString?: string
  ) => Promise<ReportTotals | null>;

  fetchReportAssessmentList: (
    organizationId: string,
    queryString?: string
  ) => Promise<Array<Assessment>>;

  fetchReportAssessmentSummary: (
    assessmentId: string,
    organizationId: string,
    queryString?: string
  ) => Promise<AssessmentSummary | null>;

  fetchReportAssessmentTotals: (
    organizationId: string,
    queryString?: string
  ) => Promise<Array<AssessmentTotals>>;

  fetchReportFoodTypes: (
    organizationId: string,
    assessmentId?: string,
    queryString?: string
  ) => Promise<Array<Category> | null>;

  fetchReportSuppliers: (
    organizationId: string,
    assessmentId?: string,
    filters?: string
  ) => Promise<Array<Supplier>>;

  fetchAverageImpactIntensityByTag: (
    organizationId: string
  ) => Promise<Array<TagAverageImpactIntensity>>;

  fetchSupportedRegions: () => Promise<Array<SupportedRegion>>;

  fetchSalesDashboardTimeSeriesData: (
    organizationId: string
  ) => Promise<SalesDashboardData | ErrorResponse | null>;

  fetchSalesDashboardRatingsData: (
    organizationId: string,
    filterParams: string
  ) => Promise<SalesDashboardData | ErrorResponse | null>;

  fetchSalesDashboardSummaryData: (
    organizationId: string,
    filterParams: string
  ) => Promise<SalesDashboardSummaryData | ErrorResponse | null>;

  fetchSalesData: (
    organizationId: string,
    filterParams: string
  ) => Promise<SalesDataResponse | ErrorResponse>;

  fetchSalesDataTimePeriods: (
    organizationId: string
  ) => Promise<SalesDataTimePeriodsResponse>;

  uploadSalesData: (
    organizationId: string,
    timePeriod: SalesDataTimePeriod,
    salesData: Array<{
      product_id: number;
      quantity_sold: number;
      price_per_item?: number;
    }>,
    filename: string
  ) => Promise<{
    success: boolean;
    file_id: string;
    records_created: number;
  }>;

  fetchRecipes: (organizationId: string) => Promise<{
    recipes: Array<{
      code: string;
      name: string;
      id: number;
    }>;
  }>;

  fetchSalesDashboardTagsData: (
    organizationId: string,
    filterParams: string
  ) => Promise<SalesDashboardTagsData | ErrorResponse | null>;

  key: unknown;
}

const DataStoreContext = React.createContext<DataStore | null>(null);

export function useDataStore(): DataStore {
  const dataStore = useContext(DataStoreContext);

  if (dataStore === null) {
    throw new Error("missing provider for DataStoreContext");
  } else {
    return dataStore;
  }
}

export const DataStoreProvider = DataStoreContext.Provider;
